import React from 'react';
import { ArenaParticipant } from '@/hooks/useLiveArenas';
import { Mic, MicOff, Video, VideoOff, Star, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface SpotlightViewProps {
  participant: ArenaParticipant;
  isHost: boolean;
  onRemoveSpotlight: () => void;
  className?: string;
}

export const SpotlightView: React.FC<SpotlightViewProps> = ({
  participant,
  isHost,
  onRemoveSpotlight,
  className,
}) => {
  return (
    <div
      className={cn(
        'relative bg-gradient-to-br from-primary/10 via-background to-accent/10 rounded-2xl p-4 border-2 border-primary/30 shadow-lg',
        'animate-in fade-in zoom-in-95 duration-300',
        className
      )}
    >
      {/* Spotlight badge */}
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-4 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5 shadow-md">
        <Star className="w-3 h-3 fill-current" />
        Spotlight
      </div>

      {/* Remove spotlight button (host only) */}
      {isHost && (
        <Button
          variant="ghost"
          size="icon"
          onClick={onRemoveSpotlight}
          className="absolute top-2 right-2 w-8 h-8 rounded-full bg-background/80 hover:bg-destructive/10"
        >
          <X className="w-4 h-4" />
        </Button>
      )}

      <div className="flex flex-col items-center gap-3 pt-4">
        {/* Large avatar with glow effect */}
        <div className="relative">
          {/* Animated glow ring */}
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-primary to-accent blur-md opacity-50 animate-pulse scale-110" />
          
          {/* Speaking indicator */}
          {!participant.is_muted && (
            <div className="absolute inset-0 rounded-full border-4 border-green-500 animate-ping opacity-50" />
          )}
          
          <img
            src={participant.profile?.photo_url || 'https://api.dicebear.com/7.x/avataaars/svg?seed=default'}
            alt={participant.profile?.name || 'Spotlighted user'}
            className="w-32 h-32 rounded-full object-cover border-4 border-primary relative z-10"
          />

          {/* Status indicators */}
          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 flex gap-1 z-20">
            <div
              className={cn(
                'p-1.5 rounded-full shadow-md',
                participant.is_muted ? 'bg-red-500' : 'bg-green-500'
              )}
            >
              {participant.is_muted ? (
                <MicOff className="w-4 h-4 text-white" />
              ) : (
                <Mic className="w-4 h-4 text-white" />
              )}
            </div>
            {participant.is_video_on !== undefined && (
              <div
                className={cn(
                  'p-1.5 rounded-full shadow-md',
                  participant.is_video_on ? 'bg-green-500' : 'bg-muted'
                )}
              >
                {participant.is_video_on ? (
                  <Video className="w-4 h-4 text-white" />
                ) : (
                  <VideoOff className="w-4 h-4 text-muted-foreground" />
                )}
              </div>
            )}
          </div>
        </div>

        {/* Name and info */}
        <div className="text-center mt-2">
          <h3 className="text-lg font-semibold text-foreground">
            {participant.profile?.name || 'Guest'}
          </h3>
        </div>
      </div>
    </div>
  );
};
